// WALLET-P1 §6.2: one company's own price versions (scope `company`), in the row's "Custom prices"
// modal. Edit opens the tariff dialog prefilled as a NEW version (versions are append-only);
// Remove retires a version not yet in effect and not already removed.
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  FieldError,
  InfoTip,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PencilIcon, PlusIcon, TagIcon, Trash2Icon } from "lucide-react";
import { useState } from "react";
import type { Api } from "../../../api/client";
import { rateText } from "../../billing/billing-money";
import { DisabledRowAction } from "../../billing/billing-parts";
import { billingKeys, type OperatorWalletView } from "../../billing/billing-queries";
import { formatDate } from "../../format";
import { problemText } from "../../ops-parts";
import { LoadError, Loading } from "../../screen-parts";
import { RowAction, RowActions } from "../../settings/row-actions";
import { TariffDialog, type TariffRates } from "./tariff-dialog";

type CompanyTariff = OperatorWalletView["tariffs"][number];

const utcToday = () => new Date().toISOString().slice(0, 10);

export function CompanyPrices({
  api,
  company,
  open,
  onClose,
}: {
  api: Api;
  company: { id: string; name: string } | null;
  open: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<{ from: TariffRates | null } | null>(null);
  const wallet = useQuery({
    queryKey: billingKeys.operatorWallet(company?.id ?? ""),
    queryFn: () =>
      api.client.operator.wallet.get({ params: { company_id: company?.id as string } }),
    enabled: open && company !== null,
  });
  const refresh = () => void qc.invalidateQueries({ queryKey: billingKeys.operator });
  const versions = (wallet.data?.tariffs ?? [])
    .filter((t) => t.scope === "company" && t.retired_at === null)
    .sort((a, b) => b.version - a.version);
  return (
    <>
      <Dialog open={open && editing === null} onOpenChange={(o) => (o ? null : onClose())}>
        <DialogContent data-slot="company-prices" className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="inline-flex items-center gap-1">
              Custom prices · {company?.name ?? ""}
              <InfoTip label="About custom prices">
                Replace the platform list for this company from the day each version takes effect.
                Versions are never changed: Edit saves a new one.
              </InfoTip>
            </DialogTitle>
          </DialogHeader>
          {wallet.isPending ? (
            <Loading label="Loading prices" rows={2} />
          ) : wallet.isError ? (
            <LoadError what="Prices" error={wallet.error} />
          ) : versions.length === 0 ? (
            <EmptyState
              variant="card"
              icon={TagIcon}
              title="No custom prices"
              description="This company pays the platform list."
            />
          ) : (
            <ul className="grid gap-1" data-slot="company-tariffs">
              {versions.map((t) => (
                <PriceRow
                  key={t.id}
                  api={api}
                  companyId={company?.id as string}
                  row={t}
                  onEdit={() => setEditing({ from: t })}
                  onDone={refresh}
                />
              ))}
            </ul>
          )}
          <DialogFooter>
            <DialogClose render={<Button variant="secondary" type="button" />}>Close</DialogClose>
            <Button type="button" onClick={() => setEditing({ from: null })}>
              <PlusIcon data-icon="inline-start" aria-hidden="true" />
              New version
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <TariffDialog
        api={api}
        open={open && editing !== null}
        onOpenChange={(o) => (o ? null : setEditing(null))}
        title={`Custom prices · ${company?.name ?? ""}`}
        from={editing?.from ?? null}
        {...(company ? { companyId: company.id } : {})}
        onSaved={refresh}
      />
    </>
  );
}

function PriceRow({
  api,
  companyId,
  row,
  onEdit,
  onDone,
}: {
  api: Api;
  companyId: string;
  row: CompanyTariff;
  onEdit: () => void;
  onDone: () => void;
}) {
  const inEffect = row.effective_from <= utcToday();
  return (
    <li
      data-tariff={row.version}
      className="group/row flex items-center justify-between gap-3 rounded-md px-2 py-1.5 hover:bg-muted/50"
    >
      <div className="min-w-0">
        <div className="truncate text-sm">
          v{row.version} · {row.label}
        </div>
        <div className="text-xs text-muted-foreground tabular-nums">
          From {formatDate(row.effective_from)} · in {rateText(row.input_per_m_micros)} / out{" "}
          {rateText(row.output_per_m_micros)} per 1M · image {rateText(row.image_micros)}
        </div>
      </div>
      <RowActions>
        <RowAction
          icon={PencilIcon}
          label={`Edit v${row.version}`}
          tip="New version from this one"
          onClick={onEdit}
        />
        {inEffect ? (
          <DisabledRowAction
            icon={Trash2Icon}
            label={`Remove v${row.version}`}
            why="In effect: versions in use are kept"
          />
        ) : (
          <RetirePrice api={api} companyId={companyId} row={row} onDone={onDone} />
        )}
      </RowActions>
    </li>
  );
}

function RetirePrice({
  api,
  companyId,
  row,
  onDone,
}: {
  api: Api;
  companyId: string;
  row: CompanyTariff;
  onDone: () => void;
}) {
  const [open, setOpen] = useState(false);
  const retire = useMutation({
    mutationFn: () =>
      api.client.operator.wallet.retireTariff({
        params: { company_id: companyId, tariff_id: row.id },
      }),
    onSuccess: () => {
      setOpen(false);
      onDone();
    },
  });
  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <RowAction
        icon={Trash2Icon}
        label={`Remove v${row.version}`}
        tip="Remove"
        onClick={() => setOpen(true)}
      />
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Remove v{row.version}?</AlertDialogTitle>
          <AlertDialogDescription>
            “{row.label}” will not take effect on {formatDate(row.effective_from)}.
          </AlertDialogDescription>
        </AlertDialogHeader>
        {retire.isError ? (
          <FieldError>{problemText(retire.error, "The version was not removed.")}</FieldError>
        ) : null}
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <Button variant="destructive" disabled={retire.isPending} onClick={() => retire.mutate()}>
            {retire.isPending ? "Removing…" : "Remove"}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
